import React, { useState, useEffect, useContext } from "react";
import { useLocation, Outlet, useNavigate, useParams } from "react-router-dom";
import styles from "../styles/WorkflowPage.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSnackbar } from "notistack";
import {
  faHeart as faHeartOutline,
  faBell as faBellOutline,
} from "@fortawesome/free-regular-svg-icons";
import {
  faGamepad,
  faDownload,
  faPen,
  faTrashAlt,
  faFileUpload,
  faPlay,
} from "@fortawesome/free-solid-svg-icons";
import {
  faHeart as faHeartFilled,
  faBell as faBellFilled,
  faChartBar,
  faFileAlt,
  faComments,
} from "@fortawesome/free-solid-svg-icons";
import { AuthContext } from "../AuthProvider";
import UserLink from "../components/UserLink.jsx";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal.jsx";
import ExampleUploadConfirmationModal from "../components/ExampleUploadConfirmationModal";
import {
  getWorkflowInfoById,
  getWorkflowByFileName,
  getWorkflowIsLiked,
  likeWorkflow,
  unlikeWorkflow,
  getUserFollowing,
  followUser,
  unfollowUser,
  deleteWorkflow,
  uploadWorkflowExample,
  replaceWorkflow,
} from "../api/api";
import RunWorkflowModal from "../components/RunWorkflowModal";

const WorkflowPage = () => {
  const { keycloak, authenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [workflowInfo, setWorkflowInfo] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [likesCount, setLikesCount] = useState(null);
  const [tagsArray, setTagsArray] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [uploadingExample, setUploadingExample] = useState(false);
  const [uploadConfirmModalOpen, setUploadConfirmModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [replacingWorkflow, setReplacingWorkflow] = useState(false);
  const [replaceFile, setReplaceFile] = useState(null);

  useEffect(() => {
    const fetchWorkflow = async () => {
      try {
        const wfInfo = await getWorkflowInfoById(id);
        const foundWorkflow = await getWorkflowByFileName(wfInfo.file);
        setWorkflowInfo(wfInfo);
        setWorkflow(foundWorkflow);
        setLikesCount(wfInfo.likes || 0);
        setFollowersCount(wfInfo.user_followers || 0);
        setTagsArray(
          typeof foundWorkflow.tags === "string"
            ? foundWorkflow.tags.split(",")
            : []
        );
        if (authenticated) {
          const userId = keycloak.tokenParsed.sub;
          setIsOwner(userId === wfInfo.user_id);
          setIsAdmin(keycloak.tokenParsed.realm_access.roles.includes("admin"));

          const userHasLiked = await getWorkflowIsLiked(id, keycloak.token);
          setLiked(userHasLiked);

          const followingUsers = await getUserFollowing(keycloak.token);
          if (
            Array.isArray(followingUsers.following) &&
            followingUsers.following.includes(wfInfo.user_id)
          ) {
            setFollowed(true);
          }
        }
      } catch (error) {
        console.error("Error fetching workflow:", error);
      }
    };

    fetchWorkflow();
  }, [id, authenticated, keycloak.token, keycloak.tokenParsed?.sub]);

  const [activeTab, setActiveTab] = useState("Workflow Card");
  const [liked, setLiked] = useState(false);

  const [followed, setFollowed] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);

  useEffect(() => {
    const pathSegments = location.pathname.split("/");
    const path = pathSegments.length > 3 ? pathSegments[3] : "";

    switch (path) {
      case "":
        setActiveTab("Workflow Card");
        break;
      case "example":
        setActiveTab("Example");
        break;
      case "community":
        setActiveTab("Discussions");
        break;
      default:
        setActiveTab("Workflow Card");
        break;
    }
  }, [location.pathname]);

  const handleTabChange = (tab) => {
    switch (tab) {
      case "Workflow Card":
        navigate(`/workflow/${workflowInfo.id}`);
        break;
      case "Example":
        navigate(`/workflow/${workflowInfo.id}/example`);
        break;
      case "Discussions":
        navigate(`/workflow/${workflowInfo.id}/community`);
        break;
      default:
        break;
    }
    setActiveTab(tab);
  };

  const handlePlaygroundClick = () => {
    const playgroundBaseUrl = process.env.REACT_APP_PLAYGROUND_URL.replace(/\/+$/, '');
    const url = `${playgroundBaseUrl}?flow=${workflowInfo.url}?count=false`;
    window.open(url, "_blank");
  };

  const handleRunClick = () => {
    setRunModalOpen(true);
  };

  const handleCloseRunModal = () => {
    setRunModalOpen(false);
  };

  const handleDownloadClick = async () => {
    let headerz = {};
    if (authenticated) {
      headerz = {
        Authorization: `Bearer ${keycloak.token}`,
      };
    }
    try {
      const response = await fetch(workflowInfo.url, {
        headers: headerz,
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = workflowInfo.file;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  const toggleLike = async () => {
    if (authenticated) {
      if (liked) {
        let resp = await unlikeWorkflow(id, keycloak.token);
        if (resp) {
          setLiked(false);
          setLikesCount((prev) => prev - 1);
        }
      } else {
        let resp = await likeWorkflow(id, keycloak.token);
        if (resp) {
          setLiked(true);
          setLikesCount((prev) => prev + 1);
        }
      }
    } else {
      keycloak.login();
    }
  };

  const toggleFollow = async () => {
    if (authenticated) {
      if (followed) {
        let resp = await unfollowUser(workflowInfo.user_id, keycloak.token);
        if (resp) {
          setFollowed(false);
          setFollowersCount((prev) => prev - 1);
        }
      } else {
        let resp = await followUser(workflowInfo.user_id, keycloak.token);
        if (resp) {
          setFollowed(true);
          setFollowersCount((prev) => prev + 1);
        }
      }
    } else {
      keycloak.login();
    }
  };

  const handleEditWorkflow = () => {
    if (authenticated && (isOwner || isAdmin)) {
      navigate(`/workflow/${id}/edit`);
    }
  };

  const handleDeleteClick = () => {
    if (authenticated && (isOwner || isAdmin)) {
      setDeleteModalOpen(true);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    try {
      const success = await deleteWorkflow(id, keycloak.token);
      setDeleteModalOpen(false);

      if (success) {
        enqueueSnackbar("Workflow deleted successfully", {
          variant: "success",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        });
        navigate("/workflows");
      } else {
        enqueueSnackbar("Failed to delete workflow. Please try again.", {
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        });
      }
    } catch (error) {
      console.error("Error deleting workflow:", error);
      enqueueSnackbar("An error occurred while deleting the workflow.", {
        variant: "error",
        anchorOrigin: {
          vertical: "top",
          horizontal: "center",
        },
      });
    }
  };

  const handleUploadExample = () => {
    if (!authenticated || !isOwner) return;

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".csv"; // Only accept CSV files

    fileInput.onchange = async (e) => {
      if (e.target.files.length === 0) return;

      const file = e.target.files[0];

      // Validate file is CSV
      if (!file.name.toLowerCase().endsWith(".csv")) {
        enqueueSnackbar("Only CSV files are accepted as examples", {
          variant: "error",
        });
        return;
      }

      // Store the selected file and show confirm modal
      setSelectedFile(file);
      setUploadConfirmModalOpen(true);
    };

    fileInput.click();
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("workflow_id", id);

    setUploadingExample(true);
    setUploadConfirmModalOpen(false);

    try {
      const response = await uploadWorkflowExample(
        id,
        formData,
        keycloak.token
      );

      enqueueSnackbar("Example file uploaded successfully", {
        variant: "success",
      });

      // Navigate to example tab to show the newly uploaded example
      navigate(`/workflow/${id}/example`);
    } catch (error) {
      console.error("Error uploading example:", error);
      enqueueSnackbar("Failed to upload example file. Please try again.", {
        variant: "error",
      });
    } finally {
      setUploadingExample(false);
      setSelectedFile(null);
    }
  };

  const handleCancelUpload = () => {
    setUploadConfirmModalOpen(false);
    setSelectedFile(null);
  };

  const handleReplaceWorkflow = () => {
    if (!authenticated || (!isOwner && !isAdmin)) return;

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".waldiez"; // Only accept waldiez files

    fileInput.onchange = async (e) => {
      if (e.target.files.length === 0) return;

      const file = e.target.files[0];

      // Validate file is waldiez
      if (!file.name.toLowerCase().endsWith(".waldiez")) {
        enqueueSnackbar("Only .waldiez files are accepted for replacement", {
          variant: "error",
        });
        return;
      }

      // Store the selected file and proceed with replacement
      setReplaceFile(file);
      await performWorkflowReplacement(file);
    };

    fileInput.click();
  };

  const performWorkflowReplacement = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    setReplacingWorkflow(true);

    try {
      const response = await replaceWorkflow(id, formData, keycloak.token);

      enqueueSnackbar("Workflow replaced successfully", {
        variant: "success",
        anchorOrigin: {
          vertical: "top",
          horizontal: "center",
        },
      });

      // Refresh the page to show updated workflow
      window.location.reload();
    } catch (error) {
      console.error("Error replacing workflow:", error);
      enqueueSnackbar(
        error.message || "Failed to replace workflow. Please try again.",
        {
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        }
      );
    } finally {
      setReplacingWorkflow(false);
      setReplaceFile(null);
    }
  };

  if (!workflow) {
    return <div></div>;
  }

  return (
    <div className={styles.workflowDetailsPage}>
      {/* First Row */}
      <div className={styles.firstRowWrapper}>
        <div className={styles.firstRow}>
          {/* Child Row 1: Workflow Name, Like, and Follow */}
          <div className={styles.childRow}>
            <div className={styles.workflowInfo}>
              <h2>{workflow.name}</h2>
              <p>
                by{" "}
                <UserLink
                  userId={workflowInfo.user_id}
                  userName={workflowInfo.user}
                />
              </p>
            </div>
            <div className={styles.likeFollow}>
              {/* Owner Actions - only show if user is the owner */}
              {(isOwner || isAdmin) && (
                <div className={styles.ownerActions}>
                  <button
                    className={styles.actionButton}
                    onClick={handleEditWorkflow}
                    title="Edit Workflow"
                  >
                    <FontAwesomeIcon icon={faPen} />
                    Edit
                  </button>
                  <button
                    className={styles.actionButton}
                    onClick={handleReplaceWorkflow}
                    disabled={replacingWorkflow}
                    title="Replace Workflow File"
                  >
                    <FontAwesomeIcon icon={faFileAlt} />
                    {replacingWorkflow ? "Replacing..." : "Replace"}
                  </button>
                  <button
                    className={styles.actionButton}
                    onClick={handleUploadExample}
                    disabled={uploadingExample}
                    title="Upload CSV Example"
                  >
                    <FontAwesomeIcon icon={faFileUpload} />
                    {uploadingExample ? "Uploading..." : "Upload Example"}
                  </button>
                  <button
                    className={styles.actionButton}
                    onClick={handleDeleteClick}
                    title="Delete Workflow"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                    Delete
                  </button>
                </div>
              )}

              {/* Like Interface */}
              <div className={styles.likeInterface} onClick={toggleLike}>
                <FontAwesomeIcon
                  icon={liked ? faHeartFilled : faHeartOutline}
                  className={styles.likeIcon}
                />
                <span className={styles.likeText}>Like</span>
                <span className={styles.likeCount}>{likesCount}</span>
              </div>
              {/* Follow Interface */}
              <div className={styles.followInterface} onClick={toggleFollow}>
                <FontAwesomeIcon
                  icon={followed ? faBellFilled : faBellOutline}
                  className={styles.followIcon}
                />
                <span className={styles.followText}>
                  {followed ? `Unfollow` : `Follow`}
                </span>
                <span className={styles.followCount}>{followersCount}</span>
              </div>
            </div>
          </div>

          {/* Child Row 2: Tags */}
          <div className={styles.childRow}>
            <div className={styles.tags}>
              {tagsArray.map((tag, index) => (
                <span
                  key={index}
                  className={styles.tag}
                  onClick={() => alert(`Search for ${tag}`)}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Child Row 3: Tabs and Buttons */}
          <div className={styles.childRow}>
            <div className={styles.tabs}>
              <button
                className={`${styles.tabButton} ${activeTab === "Workflow Card" ? styles.activeTab : ""
                  }`}
                onClick={() => handleTabChange("Workflow Card")}
              >
                <FontAwesomeIcon icon={faChartBar} className={styles.tabIcon} />
                Workflow Card
              </button>

              <button
                className={`${styles.tabButton} ${activeTab === "Example" ? styles.activeTab : ""
                  }`}
                onClick={() => handleTabChange("Example")}
              >
                <FontAwesomeIcon icon={faFileAlt} className={styles.tabIcon} />
                Example
              </button>

              <button
                className={`${styles.tabButton} ${activeTab === "Discussions" ? styles.activeTab : ""
                  }`}
                onClick={() => handleTabChange("Discussions")}
              >
                <FontAwesomeIcon icon={faComments} className={styles.tabIcon} />
                Discussions
              </button>
            </div>
            <div className={styles.actions}>
              <button
                className={styles.actionButton}
                onClick={handlePlaygroundClick}
              >
                <FontAwesomeIcon icon={faGamepad} />
                Open in Playground
              </button>
              {process.env.REACT_APP_INCLUDE_RUNNER === "true" && authenticated && (
                <button
                  className={`${styles.actionButton}`}
                  onClick={handleRunClick}
                >
                  <FontAwesomeIcon icon={faPlay} />
                  Run
                </button>
              )}
              <button
                className={styles.actionButton}
                onClick={handleDownloadClick}
              >
                <FontAwesomeIcon icon={faDownload} />
                Download
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Second Row */}
      <div className={styles.secondRow}>
        <Outlet />
      </div>

      <RunWorkflowModal
        isOpen={runModalOpen}
        onClose={handleCloseRunModal}
        workflowUrl={workflowInfo?.url}
        workflowId={workflowInfo?.id}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        workflowName={workflow?.name}
      />

      {/* Example Upload Confirmation Modal */}
      <ExampleUploadConfirmationModal
        isOpen={uploadConfirmModalOpen}
        onClose={handleCancelUpload}
        onConfirm={handleConfirmUpload}
        fileName={selectedFile?.name || ""}
      />
    </div>
  );
};

export default WorkflowPage;
